import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  ButtonGroup,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import classnames from 'classnames';
import 'suneditor/dist/css/suneditor.min.css';
import suneditor from 'suneditor';
import plugins from 'suneditor/src/plugins';
import SweetAlert from 'react-bootstrap-sweetalert';
import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const DATA = gql`
  query getProposalTemplates {
    ProposalTemplates {
      TemplateId
      Name
      Content
      TOCLine
      DefaultPosition
      SortOrder
    }
  }
`;

const ARCHIVED_TEMPLATES_DATA = gql`
  query getArchivedProposalTemplates {
    ProposalTemplates(trashed: ONLY) {
      TemplateId
      Name
      TOCLine
    }
  }
`;

const CREATE_PROPOSAL_TEMPLATE = gql`
  mutation createProposalTemplate($input: ProposalTemplateInput) {
    createProposalTemplate(input: $input) {
      TemplateId
      Name
      TOCLine
      Content
      SortOrder
      DefaultPosition
    }
  }
`;

const UPDATE_PROPOSAL_TEMPLATE = gql`
  mutation updateProposalTemplate($input: ProposalTemplateInput) {
    updateProposalTemplate(input: $input) {
      TemplateId
      Name
      TOCLine
      Content
      DefaultPosition
    }
  }
`;

const ARCHIVE_PROPOSAL_TEMPLATE = gql`
  mutation deleteProposalTemplate($TemplateId: ID!) {
    deleteProposalTemplate(TemplateId: $TemplateId) {
      TemplateId
    }
  }
`;

const REORDER_PROPOSAL_TEMPLATES = gql`
  mutation reorderProposalTemplates($input: [ProposalTemplateOrderInput]) {
    reorderProposalTemplates(input: $input) {
      TemplateId
      SortOrder
    }
  }
`;

const RESTORE_PROPOSAL_TEMPLATE = gql`
  mutation restoreProposalTemplate($TemplateId: ID!) {
    restoreProposalTemplate(TemplateId: $TemplateId) {
      TemplateId
      Name
      TOCLine
      Content
      DefaultPosition
      SortOrder
    }
  }
`;

const isLocked = (template) =>
  template.DefaultPosition === 'start' || template.DefaultPosition === 'end';

const getReorderListStyle = () => ({
  background: '#fff',
  border: '1px solid #ccc',
  padding: '4px 4px 0',
  paddingBottom: 0,
  width: '100%',
});

const getReorderItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  padding: 8,
  margin: '0 0 4px 0',
  background: '#fff',
  border: '1px solid #ccc',
  ...draggableStyle,
});

const editorOptions = {
  height: 400,
  plugins: plugins,
  placeholder: '',
  buttonList: [
    ['undo', 'redo'],
    ['font', 'fontSize', 'formatBlock'],
    ['paragraphStyle', 'blockquote'],
    ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
    '/',
    ['fontColor', 'hiliteColor', 'textStyle'],
    ['removeFormat'],
    ['outdent', 'indent'],
    ['align', 'horizontalRule', 'list', 'lineHeight'],
    ['table', 'link', 'image'],
    ['fullScreen', 'showBlocks', 'codeView'],
    ['preview'],
  ],
};

const quoteVariables = [
  { id: 'proposal_id', name: 'Proposal Number' },
  { id: 'proposal_title', name: 'Proposal Title' },
  { id: 'company_name', name: 'Company Name' },
  { id: 'customer_firstname', name: 'Customer First Name' },
  { id: 'customer_lastname', name: 'Customer Last Name' },
  { id: 'customer_email', name: 'Customer Email' },
  { id: 'customer_logo', name: 'Customer Logo' },
  { id: 'agent_firstname', name: 'Agent First Name' },
  { id: 'agent_lastname', name: 'Agent Last Name' },
  { id: 'agent_email', name: 'Agent Email' },
  { id: 'team_name', name: 'Team Name' },
  { id: 'team_members', name: 'Team Members' },
  { id: 'product_details', name: 'Product Details' },
  { id: 'quote_prepared_on', name: 'Prepared Date' },
  { id: 'quote_valid_until', name: 'Valid Until Date' },
];

const quoteOptionVariables = [
  { id: 'hardware_items', name: 'Hardware Items' },
  { id: 'contract_period', name: 'Contract Period' },
  { id: 'pricing_tables', name: 'Pricing Tables' },
  { id: 'initial_costs', name: 'Initial Costs' },
  { id: 'ongoing_costs', name: 'Monthly Costs' },
  { id: 'usage_rates', name: 'Usage Rates' },
  { id: 'proceed_button', name: 'Proceed Button' },
  { id: 'initial_cost_comparison', name: 'Initial Cost Comparison' },
  { id: 'ongoing_cost_comparison', name: 'Monthly Cost Comparison' },
  { id: 'usage_rates_comparison', name: 'Usage Rates Comparison' },
];

const useTrashedTemplatesModal = ({ onRestore }) => {
  const [open, setOpen] = useState(false);
  const [restoreProposalTemplate] = useMutation(RESTORE_PROPOSAL_TEMPLATE);

  const { error, loading, data } = useQuery(ARCHIVED_TEMPLATES_DATA, {
    skip: !open,
  });

  const toggle = () => setOpen(!open);

  const restoreTemplate = (templateId) => {
    restoreProposalTemplate({ variables: { TemplateId: templateId } }).then(({ data }) => {
      const restored = data.restoreProposalTemplate;
      onRestore(restored);
      toggle();
    });
  };

  let Body;
  if (loading || !data) {
    Body = <>Loading...</>;
  } else if (error) {
    Body = <>Error loading templates {error?.message}</>;
  } else {
    Body = (
      <div>
        <ButtonGroup vertical>
          {data.ProposalTemplates.map(({ TemplateId, Name }) => (
            <Button className="mt-1" key={TemplateId} onClick={() => restoreTemplate(TemplateId)}>
              ({TemplateId}) {Name}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    );
  }

  const trashedTemplateModal = (
    <Modal isOpen={open} toggle={toggle}>
      <ModalHeader toggle={toggle}>Archived Templates</ModalHeader>
      <ModalBody>{Body}</ModalBody>
    </Modal>
  );

  return { toggle, trashedTemplateModal };
};

const ProposalTemplates = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const [editorClicked, setEditorClicked] = useState(false);
  const [saveTemplateResult, setSaveTemplateResult] = useState(null);
  const processedCreateData = useRef(null);
  const processedUpdateData = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [templateEditors, setTemplateEditors] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [modal, setModal] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ Name: '', TOCLine: '' });
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const { data, loading } = useQuery(DATA);

  const resetTemplates = useCallback((newTemplates, tab = 0) => {
    ReactDOM.unstable_batchedUpdates(() => {
      if (templateEditors) templateEditors.forEach((e) => e.destroy());
      setTemplateEditors(null);
      setTemplates(newTemplates);
      setActiveTab(tab);
    });
  }, [templateEditors]);

  const { toggle: toggleTrashedModal, trashedTemplateModal } = useTrashedTemplatesModal({
    onRestore: (restored) => {
      const newTemplates = [...(templates || []), restored].sort((a, b) => a.SortOrder - b.SortOrder);
      resetTemplates(newTemplates, newTemplates.findIndex((t) => t.TemplateId === restored.TemplateId));
    },
  });

  const [createProposalTemplate, { data: createdData }] = useMutation(CREATE_PROPOSAL_TEMPLATE);
  const [updateProposalTemplate, { data: updatedData }] = useMutation(UPDATE_PROPOSAL_TEMPLATE);
  const [deleteProposalTemplate] = useMutation(ARCHIVE_PROPOSAL_TEMPLATE);
  const [reorderProposalTemplates] = useMutation(REORDER_PROPOSAL_TEMPLATES);

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setEditorClicked(false);
    }
  };

  const addNewTemplate = (e) => {
    e.preventDefault();
    if (newTemplate.Name && newTemplate.TOCLine) {
      setSaveTemplateResult(null);
      setModal(false);
      createProposalTemplate({
        variables: {
          input: {
            Name: newTemplate.Name,
            TOCLine: newTemplate.TOCLine,
            Content: '<div>&nbsp;</div>',
            SortOrder: templates ? templates.length : 0,
          },
        },
      });
    }
  };

  const updateTemplateItem = (value, field, index) => {
    const updated = Object.assign({}, templates[index]);
    updated[field] = value;
    const newTemplates = Array.from(templates);
    newTemplates[index] = updated;
    setTemplates(newTemplates);
  };

  const saveTemplate = (e) => {
    e.preventDefault();
    setSaveTemplateResult(null);
    const template = Object.assign({}, templates[activeTab]);
    template.Content = templateEditors[activeTab].getContents();
    updateProposalTemplate({ variables: { input: template } });
  };

  const previewTemplate = () => {
    setSaveTemplateResult({ ...saveTemplateResult, isPreview: true });
    updateProposalTemplate({
      variables: {
        input: {
          TemplateId: templates[activeTab].TemplateId,
          Preview: templateEditors[activeTab].getContents(),
        },
      },
    });
  };

  const onReorderDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const reordered = Array.from(templates);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    let newTab = activeTab;
    const activeId = templates[activeTab]?.TemplateId;
    if (activeId) newTab = reordered.findIndex((t) => t.TemplateId === activeId);

    resetTemplates(reordered, newTab >= 0 ? newTab : 0);

    reorderProposalTemplates({
      variables: {
        input: reordered.map((t, i) => ({
          TemplateId: t.TemplateId,
          SortOrder: i,
        })),
      },
    });
  };

  const archiveTemplate = (template) => setArchiveTarget(template);

  const confirmArchive = () => {
    setArchiving(true);
    deleteProposalTemplate({ variables: { TemplateId: archiveTarget.TemplateId } }).then(() => {
      resetTemplates(templates.filter((t) => t.TemplateId !== archiveTarget.TemplateId), 0);
      setArchiving(false);
      setArchiveTarget(null);
    });
  };

  useEffect(() => {
    if (!loading && data?.ProposalTemplates && templates === null) {
      setTemplates([...data.ProposalTemplates].sort((a, b) => a.SortOrder - b.SortOrder));
    }
  }, [data, loading, templates]);

  useEffect(() => {
    if (templates && templateEditors === null) {
      const editors = templates.map((_, index) =>
        suneditor.create(`template-${index}`, editorOptions)
      );
      editors.forEach((editor, index) => {
        editor.setContents(templates[index].Content || '');
      });
      setTemplateEditors(editors);
    }
  }, [templates, templateEditors]);

  useEffect(() => {
    if (templateEditors?.length > 0) {
      templateEditors[0].core.context.element.wysiwyg.blur();
      templateEditors.forEach((editor) => {
        editor.onClick = () => { if (!editorClicked) setEditorClicked(true); };
      });
    }
  }, [templateEditors, editorClicked]);

  useEffect(() => {
    if (createdData && processedCreateData.current !== createdData) {
      processedCreateData.current = createdData;
      setSaveTemplateResult({ message: 'Successfully created new template', success: true });
      setNewTemplate({ Name: '', TOCLine: '' });
      resetTemplates(
        [...(templates || []), createdData.createProposalTemplate],
        (templates || []).length
      );
    }
  }, [createdData, templates, resetTemplates]);

  useEffect(() => {
    if (updatedData && processedUpdateData.current !== updatedData && !saveTemplateResult?.isPreview) {
      processedUpdateData.current = updatedData;
      const saved = updatedData.updateProposalTemplate;
      setTemplates(templates.map((t) => (t.TemplateId === saved.TemplateId ? saved : t)));
      setSaveTemplateResult({ message: 'Successfully updated template', success: true });
    }
  }, [updatedData, saveTemplateResult, templates]);

  useEffect(() => {
    if (updatedData && saveTemplateResult?.isPreview) {
      processedUpdateData.current = updatedData;
      const templateId = updatedData.updateProposalTemplate.TemplateId;
      window.open(`${process.env['REACT_APP_API_URL']}/template-preview/${templateId}`);
      setSaveTemplateResult({ ...saveTemplateResult, isPreview: false });
    }
  }, [updatedData, saveTemplateResult]);

  return (
    <React.Fragment>
      {trashedTemplateModal}
      {archiveTarget && (
        <SweetAlert
          warning
          title="Confirm"
          onConfirm={confirmArchive}
          onCancel={() => setArchiveTarget(null)}
          showCancel={!archiving}
          showConfirm={!archiving}
          confirmBtnText="Yes"
          cancelBtnText="No"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="secondary"
        >
          {archiving ? 'Archiving...' : `Are you sure you want to archive "${archiveTarget.Name}"?`}
        </SweetAlert>
      )}
      <Modal isOpen={modal} toggle={() => setModal(!modal)}>
        <ModalHeader toggle={() => setModal(!modal)}>Add New Template</ModalHeader>
        <ModalBody>
          <Form onSubmit={saveTemplate}>
            <FormGroup>
              <Label>Template Name</Label>
              <Input
                type="text"
                placeholder="Enter template name"
                value={newTemplate.Name}
                onChange={(e) => setNewTemplate({ ...newTemplate, Name: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Table of Contents Line</Label>
              <Input
                type="text"
                placeholder="Enter ToC line"
                value={newTemplate.TOCLine}
                onChange={(e) => setNewTemplate({ ...newTemplate, TOCLine: e.target.value })}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={addNewTemplate}
            disabled={!newTemplate.Name || !newTemplate.TOCLine}
          >
            Add Template
          </Button>
        </ModalFooter>
      </Modal>
      {saveTemplateResult?.success && !saveTemplateResult?.isPreview && (
        <SweetAlert
          success
          title="Success"
          onConfirm={() => setSaveTemplateResult(null)}
          showConfirm={false}
          timeout={2500}
        >
          {saveTemplateResult.message}
        </SweetAlert>
      )}
      <div className="content">
        <div className="container-fluid">
          <div className="page-title-box">
            <Row className="align-items-center">
              <Col sm="12">
                <h4 className="page-title">Proposal Templates</h4>
              </Col>
            </Row>
          </div>
          <Row>
            <Col lg="9">
              {reorderMode ? (
                <>
                  <DragDropContext onDragEnd={onReorderDragEnd}>
                    <Droppable droppableId="template-tabs" direction="vertical">
                      {(droppableProvided, droppableSnapshot) => (
                        <div
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                          style={getReorderListStyle()}
                        >
                          {(templates || []).map((template, index) => (
                            <Draggable
                              key={template.TemplateId}
                              draggableId={String(template.TemplateId)}
                              index={index}
                            >
                              {(draggableProvided, draggableSnapshot) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  style={getReorderItemStyle(draggableSnapshot.isDragging, draggableProvided.draggableProps.style)}
                                >
                                  <i className="fas fa-grip-vertical mr-2" style={{ color: '#bbb' }} />
                                  {template.Name}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Button color="primary" className="mt-4" onClick={() => setReorderMode(false)}>
                    Finish Reordering
                  </Button>
                </>
              ) : (
                <Nav tabs>
                  {templates && templates.map((template, index) => (
                    <NavItem key={template.TemplateId} style={{ cursor: 'pointer' }}>
                      <NavLink
                        className={classnames({ active: activeTab === index })}
                        onClick={() => toggle(index)}
                      >
                        {template.Name}
                        {isLocked(template) && (
                          <i className="fas fa-lock ml-2" style={{ fontSize: '0.75em', opacity: 0.6 }} />
                        )}
                      </NavLink>
                    </NavItem>
                  ))}
                  <Button color="secondary" onClick={() => setModal(true)}>+ Add Template</Button>
                  <Button color="secondary" onClick={toggleTrashedModal}>Restore Archived</Button>
                  <Button color="secondary" onClick={() => setReorderMode(true)}>Reorder Templates</Button>
                </Nav>
              )}
              <TabContent activeTab={activeTab} className={classnames({ 'd-none': reorderMode })}>
                {templates && templates.map((template, index) => (
                  <TabPane key={index} tabId={index}>
                    <Row>
                      <Col sm="12">
                        <Card>
                          <CardBody>
                            <Label>Template Name</Label>
                            <Input
                              type="text"
                              placeholder="Enter template name"
                              value={template.Name || ''}
                              onChange={(e) => updateTemplateItem(e.target.value, 'Name', index)}
                              className="mb-4"
                              disabled={isLocked(template)}
                            />
                            <Label>Table of Contents Line</Label>
                            <Input
                              type="text"
                              placeholder="Enter Table of Contents"
                              value={template.TOCLine || ''}
                              onChange={(e) => updateTemplateItem(e.target.value, 'TOCLine', index)}
                              className="mb-4"
                              disabled={isLocked(template)}
                            />
                            {!isLocked(template) && (
                              <FormGroup check className="mb-4">
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    checked={template.DefaultPosition === 'middle'}
                                    onChange={(e) =>
                                      updateTemplateItem(
                                        e.target.checked ? 'middle' : null,
                                        'DefaultPosition',
                                        index
                                      )
                                    }
                                  />
                                  <span>Add to all new quotes</span>
                                </Label>
                              </FormGroup>
                            )}
                            <textarea id={`template-${index}`} style={{ width: '100%' }}></textarea>
                            <Button
                              color="primary"
                              className="mt-3"
                              disabled={!template.Name || !template.TOCLine || !template.Content}
                              onClick={saveTemplate}
                            >
                              Save Template
                            </Button>
                            <Button color="info" className="ml-2 mt-3" onClick={() => previewTemplate(template)}>
                              Preview Template
                            </Button>
                            {!isLocked(template) && (
                              <Button color="info" className="ml-2 mt-3" onClick={() => archiveTemplate(template)}>
                                Archive Template
                              </Button>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>
                ))}
              </TabContent>
            </Col>
            {!reorderMode && (
              <Col lg={3}>
                <Card>
                  <CardHeader>Proposal Variables</CardHeader>
                  <CardBody style={{ height: '100%', minHeight: '500px', maxHeight: '690px', overflowY: 'scroll' }}>
                    <div>
                      {quoteVariables.map((variable, index) => (
                        <div key={`var-${index}`}>
                          <ButtonGroup className="mb-2 d-flex btn-grp-variables">
                            <Button title={variable.id} color="success" disabled={!editorClicked}
                              onClick={() => templateEditors[activeTab].insertHTML(`%${variable.id}%`)}>+</Button>
                            <Button color="dark" className="d-flex px-2 w-100 text-left">{variable.id}</Button>
                            <Button color="light" className="d-flex px-2 w-100 text-left">{variable.name}</Button>
                          </ButtonGroup>
                        </div>
                      ))}
                      {[1, 2].flatMap(n => quoteOptionVariables.map((table, index) => (
                        <div key={`table-${n}-${index}`}>
                          <ButtonGroup className="mb-2 d-flex btn-grp-variables">
                            <Button title={`quote${n}_${table.id}`} color="success" disabled={!editorClicked}
                              onClick={() => templateEditors[activeTab].insertHTML(`%quote${n}_${table.id}%`)}>+</Button>
                            <Button color="dark" className="d-flex px-2 w-100 text-left">{`quote${n}_${table.id}`}</Button>
                            <Button color="light" className="d-flex px-2 w-100 text-left">Quote {n} {table.name}</Button>
                          </ButtonGroup>
                        </div>
                      )))}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(ProposalTemplates);
